/**
 * 물품 대여 예약 API (bt-service /api/bt) — 호출은 수료증 화면과 같은 api() 를 쓴다
 */
import { api } from '../btcert/api';
import type { RentalStatus, Holiday } from './logic';

export { api };

export type RentalCheck = { label: string; std: number; unit: string; type: 'qty' | 'ok' };
export type RentalItem = {
  _id: string; name: string; total: number; repair: number; maxPerOrder: number; fee: number;
  delivery: boolean; deliveryFee: number; active?: boolean; order: number; checks?: RentalCheck[];
};
export type RentalLine = { itemId: string; name: string; qty: number; fee?: number; outQty?: number | null };
export type HandoverMeta = { at: string; renter: string; staff: string; issue?: string } | null;
export type Reservation = {
  _id: string; no: string; regNo?: string; churchName: string; contact: string; phone: string; lines: RentalLine[];
  start: string; end: string; method: '방문' | '택배'; time: string; shipName?: string; shipPhone?: string; shipAddr?: string;
  purpose: string; memo: string; fee: number; shipFee: number; total: number; status: RentalStatus; paid: boolean;
  paidAt?: string | null; payer?: string; refund: string; rejectReason: string; trackOut: string; trackIn: string;
  adminMemo?: string; dateLog?: string[]; source?: string; pickup: HandoverMeta; ret: HandoverMeta; lateDays: number; createdAt: string;
};
export type PublicReservation = { _id: string; church: string; status: RentalStatus; start: string; end: string; lines: RentalLine[] };
export type RentalSettings = {
  account: string; holder: string; payDueHours: number; cancelDays: number; bizDays: number;
  visitTimes: string[]; holidays: Holiday[]; terms: string[]; staff?: string[];
};
export type CalendarData = { today: string; items: RentalItem[]; settings: RentalSettings; reservations: PublicReservation[] };
export type RentalFlag = { _id: string; regNo: string; churchName: string; type: string; lateDays: number; memo: string; penalty: string; resNo: string; createdAt: string };
export type Handover = {
  _id: string; type: 'pickup' | 'ret'; at: string; lines: Array<{ itemId: string; name: string; qty: number }>;
  checks: Array<{ item: string; label: string; type: 'qty' | 'ok'; std?: number; unit?: string; value: number | string }>;
  renter: string; renterPhone: string; staff: string; note: string; cond: string; photos: string[]; sigRenter: string; sigStaff: string;
};
export type ChurchSession = { regNo: string; churchName: string; managerName?: string; managerPhone?: string; location?: string; region?: string };

export type RequestBody = {
  lines: Array<{ itemId: string; qty: number }>; start: string; end: string; method: '방문' | '택배'; time: string;
  contact: string; phone: string; shipName: string; shipPhone: string; shipAddr: string; purpose: string; memo: string;
};

export const rentalApi = {
  // 교회
  calendar: (from: string, to: string) => api<{ data: CalendarData }>(`/church-portal/rental/calendar?from=${from}&to=${to}`).then((r) => r.data),
  churchMe: () => api<{ data: ChurchSession }>('/church-portal/me').then((r) => r.data),
  churchLookup: (regNo: string) => api<{ data: ChurchSession & { teacherCount?: number } }>(`/church-portal/lookup?regNo=${regNo}`).then((r) => r.data),
  churchLogin: (regNo: string, managerName: string, managerPhone: string) =>
    api<{ data: ChurchSession }>('/church-portal/login', { body: { regNo, managerName, managerPhone } }).then((r) => r.data),
  churchLogout: () => api('/church-portal/logout', { body: {} }),
  my: () => api<{ data: Reservation[] }>('/church-portal/rental/my').then((r) => r.data),
  create: (body: RequestBody & { agree: boolean }) => api<{ data: Reservation }>('/church-portal/rental/reservations', { body }).then((r) => r.data),
  cancel: (id: string) => api<{ data: Reservation }>(`/church-portal/rental/reservations/${id}/cancel`, { body: {} }).then((r) => r.data),
  extend: (id: string, end: string) => api<{ data: Reservation }>(`/church-portal/rental/reservations/${id}/extend`, { body: { end } }).then((r) => r.data),
  churchHandovers: (id: string) => api<{ data: Handover[] }>(`/church-portal/rental/reservations/${id}/handovers`).then((r) => r.data),
  // 관리자
  adminList: () => api<{ data: { today: string; reservations: Reservation[]; flags: RentalFlag[] } }>('/admin/rental/reservations').then((r) => r.data),
  adminCreate: (body: RequestBody & { regNo: string; churchName?: string }) => api<{ data: Reservation }>('/admin/rental/reservations', { body }).then((r) => r.data),
  approve: (id: string, payer?: string) => api(`/admin/rental/reservations/${id}/approve`, { body: { payer } }),
  reject: (id: string, reason: string) => api(`/admin/rental/reservations/${id}/reject`, { body: { reason } }),
  resolve: (id: string) => api(`/admin/rental/reservations/${id}/resolve`, { body: {} }),
  update: (id: string, body: Record<string, any>) => api<{ data: Reservation }>(`/admin/rental/reservations/${id}`, { method: 'PUT', body }).then((r) => r.data),
  handover: (id: string, type: 'pickup' | 'ret', form: FormData) =>
    api<{ data: { reservation: Reservation; lateDays: number } }>(`/admin/rental/reservations/${id}/handover/${type}`, { form }).then((r) => r.data),
  adminHandovers: (id: string) => api<{ data: Handover[] }>(`/admin/rental/reservations/${id}/handovers`).then((r) => r.data),
  items: () => api<{ data: RentalItem[] }>('/admin/rental/items').then((r) => r.data),
  createItem: () => api<{ data: RentalItem }>('/admin/rental/items', { body: {} }).then((r) => r.data),
  updateItem: (id: string, body: Partial<RentalItem>) => api<{ data: RentalItem }>(`/admin/rental/items/${id}`, { method: 'PUT', body }).then((r) => r.data),
  createFlag: (body: { regNo: string; type: string; penalty: string; memo: string }) => api('/admin/rental/flags', { body }),
  deleteFlag: (id: string) => api(`/admin/rental/flags/${id}`, { method: 'DELETE' }),
  settings: () => api<{ data: RentalSettings }>('/admin/rental/settings').then((r) => r.data),
  saveSettings: (body: Partial<RentalSettings>) => api<{ data: RentalSettings }>('/admin/rental/settings', { method: 'PUT', body }).then((r) => r.data),
};

// ───────── 표시 도우미 ─────────
const DOW = ['주일', '월', '화', '수', '목', '금', '토'];
export const won = (n?: number) => `${(n || 0).toLocaleString('ko-KR')}원`;
export const fmtShort = (s?: string) => {
  if (!s) return '—';
  const [, m, d] = s.split('-').map(Number);
  const w = new Date(`${s}T12:00:00+09:00`).getDay();
  return `${m}/${d}(${DOW[w].slice(0, 1)})`;
};
export const fmtLong = (s?: string) => {
  if (!s) return '—';
  const w = new Date(`${s}T12:00:00+09:00`).getDay();
  return `${s.replace(/-/g, '.')} (${DOW[w]})`;
};
export const lineText = (lines: RentalLine[]) => lines.map((l) => `${l.name} ${l.qty}`).join(', ');
export const payDeadline = (r: Reservation, s?: RentalSettings) => new Date(new Date(r.createdAt).getTime() + (s?.payDueHours || 24) * 3600 * 1000);
export const DOW_LABELS = DOW;
